import { useEffect, useRef, useState } from 'react';
import { Button, Spin } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import type { PDFDocumentProxy } from 'pdfjs-dist';

const MAX_CSS_WIDTH = 860;

/** Draws one page at a time onto a canvas. `onFailed` covers a damaged or password-protected file. */
export function PdfPreview({ blob, onFailed }: { blob: Blob; onFailed: () => void }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [drawn, setDrawn] = useState(0);

  useEffect(() => {
    let live = true;
    let opened: PDFDocumentProxy | null = null;
    void (async () => {
      try {
        const [{ openPdf }, data] = await Promise.all([import('./pdfEngine'), blob.arrayBuffer()]);
        opened = await openPdf(data);
        if (live) setPdf(opened);
        else void opened.destroy();
      } catch {
        if (live) onFailed();
      }
    })();
    return () => {
      live = false;
      void opened?.destroy();
    };
  }, [blob, onFailed]);

  useEffect(() => {
    if (!pdf) return undefined;
    let cancel: (() => void) | null = null;
    let live = true;
    void (async () => {
      try {
        const page = await pdf.getPage(pageNumber);
        const target = canvas.current;
        const context = target?.getContext('2d');
        if (!live || !target || !context) return;
        const ratio = window.devicePixelRatio || 1;
        const base = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: (Math.min(MAX_CSS_WIDTH, base.width * 1.5) / base.width) * ratio });
        target.width = Math.floor(viewport.width);
        target.height = Math.floor(viewport.height);
        target.style.width = `${Math.floor(viewport.width / ratio)}px`;
        const task = page.render({ canvasContext: context, viewport });
        cancel = () => task.cancel();
        await task.promise;
        if (live) setDrawn(pageNumber);
      } catch (e) {
        // Turning the page cancels the draw in progress; that is not a failure of the file.
        if (live && (e as { name?: string }).name !== 'RenderingCancelledException') onFailed();
      }
    })();
    return () => {
      live = false;
      cancel?.();
    };
  }, [pdf, pageNumber, onFailed]);

  const pages = pdf?.numPages ?? 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      {drawn !== pageNumber && <Spin aria-label="Drawing the page" />}
      {pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button size="small" icon={<LeftOutlined aria-hidden />} aria-label="Previous page" disabled={pageNumber <= 1} onClick={() => setPageNumber((n) => n - 1)} data-testid="pdf-prev" />
          <span data-testid="pdf-page">Page {pageNumber} of {pages}</span>
          <Button size="small" icon={<RightOutlined aria-hidden />} aria-label="Next page" disabled={pageNumber >= pages} onClick={() => setPageNumber((n) => n + 1)} data-testid="pdf-next" />
        </div>
      )}
      <canvas
        ref={canvas}
        role="img"
        aria-label={`Document, page ${pageNumber}${pages ? ` of ${pages}` : ''}`}
        data-testid="pdf-canvas"
        data-drawn-page={drawn}
        style={{ maxWidth: '100%', background: '#fff' }}
      />
    </div>
  );
}
